<template>
  <v-card class="edit profile elevation-4" dark v-if="profile">
    <can I="edit" a="Profile">
      <v-card-title primary-title>
        <v-avatar class="elevation-2 mr-3">
          <img :src="profile.avatar" />
        </v-avatar>
        <div>
          <div class="headline">{{ profile.firstNames }} {{ profile.lastName }}</div>
          <span v-if="profile.candidate">
            {{ profile.candidate.email }}
          </span>
        </div>
      </v-card-title>

      <v-alert error transition="scale-transition" :value="profile.errors">
        <ul>
          <li v-for="message in profile.errors" :key="message">{{ message }}</li>
        </ul>
      </v-alert>

      <v-divider />

      <v-card-text>
        <v-form v-model="valid" @submit.prevent="submit">
          <CandidateSelect
            :value="profile.candidate || { email: '' }"
            @input="candidate => (profile.candidate = candidate)"
          />

          <v-text-field
            label="First Names"
            v-model="profile.firstNames"
            required :rules="rules.firstNames"
          />

          <v-text-field
            label="Last Name"
            v-model="profile.lastName"
            required :rules="rules.lastName"
          />

          <v-select
            label="Gender"
            v-model="profile.gender"
            :items="['Male', 'Female', 'Other']"
          />

          <v-select
            label="Ethnicity"
            v-model="profile.ethnicity"
            :items="ethnicities"
          />

          <date-picker
            label="Date of Birth"
            v-model="profile.bornAt"
            required :rules="rules.bornAt"
            append-icon="mdi-calendar"
            clearable
          />

          <v-autocomplete
            label="Country of Birth"
            v-model="profile.countryOfBirth"
            :items="countries" item-text="name" item-value="code"
            required :rules="rules.countryOfBirth"
          />

          <v-text-field
            label="Identification"
            v-model="profile.identification"
            required :rules="rules.identification"
          />

          <DocumentUpload
            label="Identity Document"
            :action="`/admin/profiles/${profile.id}/identity_document`"
            :value="profile.identityDocument"
            @submit="$store.dispatch('admin/profiles/fetch', profile.id)"
            auto-submit
          />

          <v-select
            label="Has a driver's licence"
            v-model="profile.driversLicense"
            :items="[{ value: true, text: 'Yes' }, { value: false, text: 'No' }]"
          />

          <MobileNumber v-model="profile.mobileNumber" @validate="(e) => validMobile = e" />

          <v-text-field
            label="Current address"
            v-model="profile.location.address"
            hint="This should be your street address"
          />

          <v-combobox multiple chips
            label="Interests"
            v-model="profile.interests"
            :items="[]"
            append-icon=""
          >
            <v-chip slot="selection" slot-scope="selection">
              {{ selection.item }}
            </v-chip>
          </v-combobox>

          <v-combobox multiple chips
            label="Personal Traits"
            v-model="profile.behavioralCompetencies"
            v-if="behavioralCompetencies.length > 0"
            :items="behavioralCompetencies"
            item-text="name" item-value="id"
            return-object
          >
            <v-chip
              slot="selection"
              slot-scope="data"
              @input="removeBehavioralCompetency"
              close
            >
              {{ data.item.name }}
            </v-chip>
          </v-combobox>

          <v-combobox multiple chips
            label="Technical Competencies"
            v-model="profile.technicalCompetencies"
            :items="technicalCompetencies"
          >
            <v-chip slot="selection" slot-scope="selection">
              {{ selection.item }}
            </v-chip>
          </v-combobox>

          <v-combobox multiple chips
            label="Professional Tools"
            v-model="profile.professionalTools"
            :items="[]"
            append-icon=""
          >
            <v-chip slot="selection" slot-scope="selection">
              {{ selection.item }}
            </v-chip>
          </v-combobox>

          <v-textarea box
            label="Biography"
            v-model="profile.biography"
            :hint="biographyErrors"
            auto-grow
            rows="2"
            outline
          />

          <v-checkbox v-model="profile.receiveDigests" label="Receive Digests" />
          <v-checkbox
            label="Candidate has a substantially limiting disability"
            v-model="profile.disabled"
          />

          <v-select v-if="profile.disabled == true"
            label="Indicate the broad category of the impairment if available"
            v-model="profile.disabilityCategory"
            :items="disabilityCategories"
          />
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn flat @click="$emit('cancel')" color="grey">
          Cancel
        </v-btn>
        <v-btn color="primary" :disabled="!valid || !validMobile" @click="submit">
          Update Profile
        </v-btn>
      </v-card-actions>
    </can>
  </v-card>
</template>

<script>
// Components
import CandidateSelect from '@/components/selects/CandidateSelect'
import MobileNumber from '@/components/profiles/MobileNumber'
import DocumentUpload from '@/components/DocumentUpload'

// Data
import countries from '@/data/countries'
import disabilityCategories from '@/data/disability_categories'
import ethnicities from '@/data/ethnicities'
import technicalCompetencies from '@/data/technical_competencies'

// Helpers
import { LuhnCheck } from '@/mixins/LuhnCheck'
import { mapGetters, mapState } from 'vuex'

// Vue Component
export default {
  mixins: [LuhnCheck],
  components: { CandidateSelect, DocumentUpload, MobileNumber },
  created() {
    this.$store.dispatch('behavioralCompetencies/fetchAll')
  },
  data() {
    return {
      // Data
      countries,
      disabilityCategories: Object.keys(disabilityCategories).map(k => ({ value: parseInt(k), text: disabilityCategories[k] })),
      ethnicities,
      technicalCompetencies,
      // Validations
      valid: true,
      validMobile: true,
      rules: {
        firstNames: [v => !!v || 'You must specify a first name'],
        lastName: [v => !!v || 'You must specify a last name'],
        bornAt: [],
        countryOfBirth: [v => !!v || 'You must specify a country of identification'],
        identification: [
          v => !!v || 'You must specify a method of identification',
          v =>
            this.profile.countryOfBirth !== 'ZA' ||
            this.LuhnCheck(v) ||
            'You must specify a valid identification',
        ],
      },
    }
  },
  computed: { ...mapGetters({
      profile: 'admin/profiles/find',
      behavioralCompetencies: 'behavioralCompetencies/all',
    }),
    biographyErrors() {
      if (_.get(this, 'profile.biography.length') === 0) return ""
      if (this.wordCount(this.profile.biography) >= 150) return ""

      return `150 words required (currently  ${this.wordCount(this.profile.biography)})`
    },
  },
  methods: {
    wordCount(text) {
      if (typeof text !== 'string') return 0
      return ((text || '').match(/([a-zA-Z0-9_\-'’.,])+/gm) || []).length
    },
    submit() {
      if (this.valid && this.validMobile) this.$emit('update')
    },
    removeBehavioralCompetency(item) {
      this.profile.behavioralCompetencies.splice(
        this.profile.behavioralCompetencies.indexOf(item),
        1
      )
      this.profile.behavioralCompetencies = [...this.profile.behavioralCompetencies]
    },
  },
}
</script>
